import { type ReactNode, useState } from "react";
import { Package, Truck } from "@/components/icons";
import { Skeleton } from "@/components/kit";
import type { StockValuationDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { ReportTermHint } from "./ReportTermHint";

type StockValuationSummaryCardsProps = {
  summary?: StockValuationDTO;
  isLoading?: boolean;
  collapsibleExtra?: boolean;
};

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  accentClass,
}: {
  icon: typeof Package;
  label: ReactNode;
  value: string;
  sub?: ReactNode;
  accentClass: string;
}) {
  return (
    <div className="brand-card flex items-center gap-3 p-4">
      <div
        className={`flex size-10 shrink-0 items-center justify-center rounded-xl ${accentClass}`}
      >
        <Icon />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-muted-foreground text-xs">{label}</span>
        <span className="font-display text-xl tabular-nums leading-tight sm:text-2xl">
          {value}
        </span>
        {sub ? (
          <span className="text-muted-foreground text-xs">{sub}</span>
        ) : null}
      </div>
    </div>
  );
}

export function StockValuationSummaryCards({
  summary,
  isLoading,
  collapsibleExtra = true,
}: StockValuationSummaryCardsProps) {
  const [showExtra, setShowExtra] = useState(false);

  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {["a", "b", "c", "d"].map((id) => (
          <Skeleton key={id} className="h-24 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!summary) return null;

  const mobileHideExtra = collapsibleExtra && !showExtra;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={Package}
          label="ມູນຄ່າສະຕັອກຄົງເຫລຼືອ"
          value={formatLak(summary.onHandValueLak)}
          sub={
            <ReportTermHint
              label="lot × ຕົ້ນທຶນຕ່ອຫນ່ວຍ"
              hint="FIFO unit landed cost × ຈຳນວນຄົງເຫລຼືອ"
            />
          }
          accentClass="bg-leaf/15 text-leaf"
        />
        <StatCard
          icon={Truck}
          label="ມູນຄ່າລະຫວ່າງທາງ"
          value={formatLak(summary.inTransitValueLak)}
          sub="import orders ທີ່ຍັງຄ້າງຮັບ"
          accentClass="bg-secondary/15 text-secondary"
        />
        <StatCard
          icon={Package}
          label="ມູນຄ່າຄລັງລວມ"
          value={formatLak(summary.totalInventoryValueLak)}
          sub="ຄົງເຫລຼືອ + ລະຫວ່າງທາງ"
          accentClass="bg-primary/15 text-secondary"
        />
        <div className={mobileHideExtra ? "hidden sm:block" : undefined}>
          <StatCard
            icon={Package}
            label="ຕົວເລືອກມີມູນຄ່າ"
            value={String(summary.variantCount)}
            sub="ມີສະຕັອກ ຫລື incoming"
            accentClass="bg-accent/20 text-foreground"
          />
        </div>
      </div>
      {mobileHideExtra ? (
        <button
          type="button"
          onClick={() => setShowExtra(true)}
          className="self-start rounded-full border border-border bg-background px-3 py-1.5 font-medium text-muted-foreground text-xs hover:bg-muted/60 sm:hidden"
        >
          ເບິ່ງເພີ່ມ (ຈຳນວນ variant)
        </button>
      ) : null}
    </div>
  );
}
